import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';
import apiConfig from '@/application/config/api';

const streamerAPI: AxiosInstance = createAxiosInstance({
  baseURL: `${apiConfig.GAME_CATALOG_API_BASE_URL}/business/streamers`,
  baseUrlKey: 'GAME_CATALOG_API_BASE_URL',
});
attachAuthInterceptors(streamerAPI);

export interface StreamerData {
  id: string;
  business_profile_id: string;
  name: string;
  code: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetStreamersResponse {
  success: boolean;
  data?: {
    data: StreamerData[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
  message?: string;
}

export interface CreateStreamerRequest {
  name: string;
  code: string;
}

export interface UpdateStreamerRequest {
  name: string;
  code: string;
  is_active: boolean;
}

export interface StreamerResponse {
  success: boolean;
  data?: StreamerData;
  message?: string;
}

export interface DeleteStreamerResponse {
  success: boolean;
  message?: string;
}

// Commission types
export interface StreamerCommission {
  id: string;
  business_streamer_id: string;
  game_package_id: string;
  game_package_name?: string;
  commission_rate: number;
  commission_type: 'FIXED' | 'PERCENT';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PackageWithCommission {
  game_id: string;
  game_name: string;
  package_id: string;
  package_name: string;
  custom_price: number | null;
  platform_price?: number | null;
  has_commission: boolean;
  commission_id: string | null;
  commission_rate: number;
  commission_type: 'FIXED' | 'PERCENT';
  is_active: boolean;
}

export interface GameWithCommissionsResponse {
  success: boolean;
  data?: {
    game_id: string;
    game_name: string;
    streamer_id: string;
    packages: PackageWithCommission[];
  };
  message?: string;
}

export interface ListCommissionsResponse {
  success: boolean;
  data?: {
    data: StreamerCommission[];
    total: number;
  };
  message?: string;
}

export interface UpsertCommissionResponse {
  success: boolean;
  data?: StreamerCommission;
  message?: string;
}

export const getStreamers = async (params?: {
  page?: number;
  limit?: number;
  keyword?: string;
}): Promise<GetStreamersResponse> => {
  try {
    const response = await streamerAPI.get('/', { params });
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const getStreamer = async (id: string): Promise<StreamerResponse> => {
  try {
    const response = await streamerAPI.get(`/${id}`);
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const createStreamer = async (data: CreateStreamerRequest): Promise<StreamerResponse> => {
  try {
    const response = await streamerAPI.post('/', data);
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const updateStreamer = async (id: string, data: UpdateStreamerRequest): Promise<StreamerResponse> => {
  try {
    const response = await streamerAPI.put(`/${id}`, data);
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const deleteStreamer = async (id: string): Promise<DeleteStreamerResponse> => {
  try {
    await streamerAPI.delete(`/${id}`);
    return { success: true };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

// Commission endpoints
export const getStreamerCommissions = async (
  streamerId: string
): Promise<ListCommissionsResponse> => {
  try {
    const response = await streamerAPI.get(`/${streamerId}/commissions`);
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const getPackagesWithCommissions = async (
  streamerId: string,
  gameId: string
): Promise<GameWithCommissionsResponse> => {
  try {
    const response = await streamerAPI.get(`/${streamerId}/commissions/packages`, {
      params: { game_id: gameId },
    });
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const upsertStreamerCommission = async (
  streamerId: string,
  data: { game_package_id: string; commission_rate: number; commission_type: 'FIXED' | 'PERCENT' }
): Promise<UpsertCommissionResponse> => {
  try {
    const response = await streamerAPI.post(`/${streamerId}/commissions/upsert`, data);
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const deleteStreamerCommission = async (
  streamerId: string,
  commissionId: string
): Promise<DeleteStreamerResponse> => {
  try {
    await streamerAPI.delete(`/${streamerId}/commissions/${commissionId}`);
    return { success: true };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const toggleStreamerCommission = async (
  streamerId: string,
  commissionId: string,
  isActive: boolean
): Promise<DeleteStreamerResponse> => {
  try {
    await streamerAPI.patch(`/${streamerId}/commissions/${commissionId}/toggle`, {
      is_active: isActive,
    });
    return { success: true };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export interface BulkUpsertCommissionResponse {
  success: boolean;
  data?: { upserted: number };
  message?: string;
}

export const bulkUpsertStreamerCommissions = async (
  streamerId: string,
  items: { game_package_id: string; commission_rate: number; commission_type: 'FIXED' | 'PERCENT' }[]
): Promise<BulkUpsertCommissionResponse> => {
  try {
    const response = await streamerAPI.post(`/${streamerId}/commissions/bulk-upsert`, { items });
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export interface CopyFromResponse {
  success: boolean;
  data?: { copied: number };
  message?: string;
}

export const applyAllGamesCommission = async (
  streamerId: string,
  commissionRate: number,
  commissionType: 'FIXED' | 'PERCENT'
): Promise<BulkUpsertCommissionResponse> => {
  try {
    const response = await streamerAPI.post(`/${streamerId}/commissions/apply-all`, {
      commission_rate: commissionRate,
      commission_type: commissionType,
    });
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const copyStreamerCommissions = async (
  targetStreamerId: string,
  sourceStreamerId: string
): Promise<CopyFromResponse> => {
  try {
    const response = await streamerAPI.post(`/${targetStreamerId}/commissions/copy-from`, {
      source_streamer_id: sourceStreamerId,
    });
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

// --- Streamer Order Report ---

export interface StreamerOrderItem {
  order_no: string;
  completed_at: string | null;
  game_name: string | null;
  package_name: string | null;
  customer_price: number | null;
  cost: number;
  profit: number;
  commission_rate: number;
  commission_type: 'PERCENT' | 'FIXED';
  commission_earned: number;
  customer_account_code: string | null;
  customer_email: string | null;
}

export interface GetStreamerOrderReportParams {
  streamer_id: string;
  page?: number;
  limit?: number;
  completed_at_from?: string;
  completed_at_to?: string;
  game_package_id?: string;
}

export interface GetStreamerOrderReportResponse {
  success: boolean;
  data?: {
    data: StreamerOrderItem[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
    total_commission: number;
  };
  message?: string;
}

export const getStreamerOrderReport = async (
  params: GetStreamerOrderReportParams
): Promise<GetStreamerOrderReportResponse> => {
  try {
    const response = await streamerAPI.get(`/${params.streamer_id}/order-report`, {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 10,
        completed_at_from: params.completed_at_from,
        completed_at_to: params.completed_at_to,
        game_package_id: params.game_package_id || undefined,
      },
    });
    return { success: true, data: response.data };
  } catch (error: any) {
    return { success: false, message: error?.response?.data?.error || error.message };
  }
};

export const exportStreamerOrderReport = async (
  streamerId: string,
  format: 'csv' | 'xlsx',
  params?: { completed_at_from?: string; completed_at_to?: string; game_package_id?: string }
): Promise<Blob> => {
  const response = await streamerAPI.get(`/${streamerId}/order-report/export`, {
    params: { format, ...params },
    responseType: 'blob',
  });
  return response.data;
};
